import * as Three from 'three';

// "Points → real room" intro, like World Labs Marble: the model first appears as glowing points coloured from the
// rooms' own photos, assembling in a wave from where you stand, then the real surfaces fade in over them.
export const REVEAL_MS = 1800;
const ASSEMBLE_MS = 1300;      // the wave reaches the farthest point
const FADE_FROM_MS = 1000;     // real surfaces start to fade in, points start to fade out
const MAX_POINTS = 250000;     // across all rooms
const WAVE_M = 1.5;            // width of the assembling wave front
const SCATTER_M = 0.8;         // how far a point starts from its place

const ease = t => t * t * (3 - 2 * t);
const clamp01 = t => Math.min(1, Math.max(0, t));

// Where the intro is at `ms` after it started: points' wave progress and alpha, real surfaces' opacity.
export function revealState(ms, duration = REVEAL_MS) {
  const k = duration / REVEAL_MS;
  const fade = ease(clamp01((ms - FADE_FROM_MS * k) / ((REVEAL_MS - FADE_FROM_MS) * k)));
  return {
    progress: ease(clamp01(ms / (ASSEMBLE_MS * k))),
    points: 1 - fade,
    surfaces: fade,
    done: ms >= duration,
  };
}

// Keep every `step`-th vertex so the total stays under `cap`.
export function pointStep(total, cap = MAX_POINTS) {
  return Math.max(1, Math.ceil(total / cap));
}

const VERTEX = `
uniform float uProgress;
uniform float uMaxDist;
uniform float uSize;
uniform vec3 uOrigin;
attribute vec3 aRandom;
varying vec2 vUv;
varying float vAlpha;
void main() {
  vUv = uv;
  vec4 world = modelMatrix * vec4(position, 1.0);
  float front = uProgress * (uMaxDist + ${WAVE_M.toFixed(2)});
  float k = clamp((front - distance(world.xyz, uOrigin)) / ${WAVE_M.toFixed(2)}, 0.0, 1.0);
  float e = k * k * (3.0 - 2.0 * k);
  vec4 mv = viewMatrix * vec4(world.xyz + aRandom * (1.0 - e) * ${SCATTER_M.toFixed(2)}, 1.0);
  vAlpha = e;
  gl_Position = projectionMatrix * mv;
  gl_PointSize = uSize * (0.7 + 0.8 * (1.0 - e));
}`;

const FRAGMENT = `
uniform sampler2D map;
uniform float uFade;
varying vec2 vUv;
varying float vAlpha;
void main() {
  float r = length(gl_PointCoord - 0.5);
  if (r > 0.5 || vAlpha <= 0.0) discard;
  float glow = 1.0 - smoothstep(0.0, 0.5, r);
  vec3 colour = texture2D(map, vUv).rgb + 0.25 * glow * (1.0 - vAlpha);
  gl_FragColor = vec4(colour, vAlpha * uFade * (0.35 + 0.65 * glow));
}`;

/**
 * Point clouds of the given meshes (each coloured from its own texture), in one scene to draw before the real
 * surfaces. Attributes are copied, so disposing the reveal never touches the meshes' GPU buffers.
 */
export default class Reveal {
  constructor(meshes) {
    this.scene = new Three.Scene();
    this._materials = [];
    const total = meshes.reduce((n, m) => n + m.geometry.attributes.position.count, 0);
    const step = pointStep(total);
    meshes.forEach((mesh) => {
      const pos = mesh.geometry.attributes.position;
      const uv = mesh.geometry.attributes.uv;
      if (!pos || !uv || !mesh.material.map) return;
      const n = Math.floor(pos.count / step);
      const p = new Float32Array(n * 3);
      const t = new Float32Array(n * 2);
      const r = new Float32Array(n * 3);
      for (let i = 0; i < n; i += 1) {
        const j = i * step;
        p[i * 3] = pos.getX(j); p[i * 3 + 1] = pos.getY(j); p[i * 3 + 2] = pos.getZ(j);
        t[i * 2] = uv.getX(j); t[i * 2 + 1] = uv.getY(j);
        const a = Math.random() * Math.PI * 2;
        const z = Math.random() * 2 - 1;
        const s = Math.sqrt(1 - z * z);
        r[i * 3] = s * Math.cos(a); r[i * 3 + 1] = z; r[i * 3 + 2] = s * Math.sin(a);
      }
      const geometry = new Three.BufferGeometry();
      geometry.setAttribute('position', new Three.BufferAttribute(p, 3));
      geometry.setAttribute('uv', new Three.BufferAttribute(t, 2));
      geometry.setAttribute('aRandom', new Three.BufferAttribute(r, 3));
      const material = new Three.ShaderMaterial({
        uniforms: {
          map: { value: mesh.material.map }, uProgress: { value: 0 }, uMaxDist: { value: 10 },
          uSize: { value: 6 }, uFade: { value: 1 }, uOrigin: { value: new Three.Vector3() },
        },
        vertexShader: VERTEX,
        fragmentShader: FRAGMENT,
        transparent: true,
        depthWrite: false,
      });
      const points = new Three.Points(geometry, material);
      points.matrixAutoUpdate = false;
      mesh.updateWorldMatrix(true, false);
      points.matrix.copy(mesh.matrixWorld);
      points.frustumCulled = false;
      this.scene.add(points);
      this._materials.push(material);
    });
  }

  // origin: where the wave starts; maxDist: to the farthest point; size: dot size in pixels.
  update(state, origin, maxDist, size) {
    this._materials.forEach((m) => {
      m.uniforms.uProgress.value = state.progress;
      m.uniforms.uFade.value = state.points;
      m.uniforms.uOrigin.value.copy(origin);
      m.uniforms.uMaxDist.value = maxDist;
      m.uniforms.uSize.value = size;
    });
  }

  dispose() {
    this.scene.traverse((node) => {
      if (!node.isPoints) return;
      node.geometry.dispose();
      node.material.dispose();
    });
    this._materials = [];
  }
}
